import React, { useEffect, useState } from 'react';

import { Box, FormControlLabel, InputAdornment, Typography } from '@material-ui/core';

import { AppButton } from '@vyce/core/src/components/AppButton';
import { AppCheckbox, AppSwitch, AppTextField } from '@vyce/core/src/components/inputs';
import {
  VatPendingStatus,
  VatUnverifiedStatus,
  VatVerifiedStatus,
} from '@vyce/core/src/components/controlled-inputs/VatNumberField';
import { DRC_REASON_CODES, DRCProfile, DRCProfilePayload, DRCStatus } from '@vyce/core/src/types';
import { getUKFormattedDate } from '@vyce/core/src/utils/dates';

import { useStyles } from '../styles';

interface Props {
  profile: DRCProfile | null;
  status: DRCStatus | null;
  saving: boolean;
  /** An HMRC check is in flight and the panel is waiting for its answer. */
  checking?: boolean;
  onSave: (payload: DRCProfilePayload) => Promise<boolean>;
  /** Absent when there is no pay employee to run the HMRC check against. */
  onVerify?: (vatNumber: string) => Promise<boolean>;
}

/**
 * VAT registration and the CIS tick. `vat_registration_date` and `vat_scheme` are shown but not
 * edited — they are what the HMRC check wrote, so typing over them would claim a verification
 * nobody performed.
 */
export const VATAndCISSection: React.FC<Props> = ({
  profile,
  status,
  saving,
  checking,
  onSave,
  onVerify,
}) => {
  const classes = useStyles();
  const [vatRegistered, setVatRegistered] = useState(false);
  const [vatNumber, setVatNumber] = useState('');
  const [cisInScope, setCisInScope] = useState(false);

  useEffect(() => {
    setVatRegistered(!!profile?.vat_registered);
    setVatNumber(profile?.vat_number || '');
    setCisInScope(!!profile?.cis_in_scope);
  }, [profile]);

  const vatNotVerified =
    !!profile?.vat_registered &&
    (status?.reason_code === DRC_REASON_CODES.VAT_NOT_VERIFIED ||
      !!status?.blockers?.includes(DRC_REASON_CODES.VAT_NOT_VERIFIED));

  const trimmedVatNumber = vatNumber.trim();
  const changed =
    vatRegistered !== !!profile?.vat_registered ||
    trimmedVatNumber !== (profile?.vat_number || '') ||
    cisInScope !== !!profile?.cis_in_scope;

  // Verified only while the stored number is the one on screen — an edit is unverified until saved and checked.
  const vatVerified =
    !!status &&
    !!profile?.vat_registered &&
    !!profile?.vat_number &&
    !vatNotVerified &&
    trimmedVatNumber === profile.vat_number;

  // Every write is partial, so only what the Legend actually touched is sent — anything left
  // out keeps its stored value.
  const buildPayload = () => {
    const payload: DRCProfilePayload = {};
    if (vatRegistered !== !!profile?.vat_registered) payload.vat_registered = vatRegistered;
    if (trimmedVatNumber !== (profile?.vat_number || '')) payload.vat_number = trimmedVatNumber || null;
    if (cisInScope !== !!profile?.cis_in_scope) payload.cis_in_scope = cisInScope;
    return payload;
  };

  const handleSave = () => onSave(buildPayload());

  const vatStatusBadge = !trimmedVatNumber ? null : checking ? (
    <VatPendingStatus />
  ) : vatVerified ? (
    <VatVerifiedStatus />
  ) : (
    <VatUnverifiedStatus />
  );

  // The check vouches for the stored number, so pending edits are saved first — otherwise HMRC
  // would verify one number while the profile holds another.
  const handleVerify = async () => {
    if (!onVerify || !trimmedVatNumber) return;
    if (changed && !(await onSave(buildPayload()))) return;
    onVerify(trimmedVatNumber);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={12}>
        <Box>
          <Typography variant="subtitle2">VAT registered</Typography>
          <Typography className={classes.hint}>
            The subcontractor (sole trader or limited company) is registered for VAT.
          </Typography>
        </Box>
        <AppSwitch
          checked={vatRegistered}
          color="primary"
          onChange={e => setVatRegistered(e.target.checked)}
        />
      </Box>

      {vatRegistered && (
        <Box marginTop={2} display="flex" flexDirection="column" gridGap={8}>
          <Box display="flex" alignItems="flex-start" gridGap={8} flexWrap="wrap">
            <Box flex={1} minWidth={220}>
              <AppTextField
                label="VAT number"
                value={vatNumber}
                fullWidth
                onChange={e => setVatNumber(e.target.value)}
                InputProps={{
                  endAdornment: vatStatusBadge && (
                    <InputAdornment position="end">{vatStatusBadge}</InputAdornment>
                  ),
                }}
              />
            </Box>
            {onVerify && (
              <AppButton
                variant="outlined"
                color="primary"
                loading={saving || checking}
                disabled={!trimmedVatNumber}
                style={{ height: 56 }}
                onClick={handleVerify}>
                Verify
              </AppButton>
            )}
          </Box>
          {/*
            A verification vouches for one number, so saving a different one drops the profile
            back to unverified until the worker service re-checks it with HMRC.
          */}
          <Typography className={classes.hint}>
            Changing the number clears the HMRC verification until it is checked again.
          </Typography>

          {(profile?.vat_registration_date || profile?.vat_scheme) && (
            <Typography className={classes.hint}>
              {[
                profile.vat_registration_date
                  ? `Registered ${getUKFormattedDate(profile.vat_registration_date)}`
                  : null,
                profile.vat_scheme ? `${profile.vat_scheme} scheme` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </Box>
      )}

      <Box marginTop={2}>
        <FormControlLabel
          control={
            <AppCheckbox
              color="primary"
              checked={cisInScope}
              onChange={e => setCisInScope(e.target.checked)}
            />
          }
          label="Working within construction (CIS) – Apply DRC (Domestic Reverse Charge)"
        />
        <Typography className={classes.hint}>
          When the reverse charge applies, Vyce does not pay the VAT to this subcontractor. It is calculated
          and shown, and V Pay Tech accounts for it to HMRC.
        </Typography>
      </Box>

      <Box marginTop={2}>
        <AppButton
          variant="contained"
          color="primary"
          size="small"
          loading={saving}
          disabled={!changed}
          onClick={handleSave}>
          Save VAT &amp; CIS
        </AppButton>
      </Box>
    </Box>
  );
};
